import React, { useMemo } from 'react';
import type { TableColumnDef } from '../../../shared/table/types.ts';
import { AppDataTable } from '../../../shared/table/AppDataTable.tsx';
import { useCfoRepeatEscalated } from '../../../hooks/analytics/useCfoReports.ts';
import { RefreshMeta, StatusChip } from '../controller/shared.tsx';
import type { CFORepeatEscalatedRow } from '../../../request/analytics/cfoReports.ts';

/**
 * Report #7 — Repeat & Escalated Issues. Amber chip for repeat, red chip for
 * escalated. Accepts an optional `rows` override so the CFO dashboard can
 * feed it slicer-filtered rows (it's the persona's primary drill-down
 * dataset); standalone usage falls back to its own fetch.
 */
export const RepeatEscalatedTable: React.FC<{ rows?: CFORepeatEscalatedRow[] }> = ({ rows: rowsOverride }) => {
  const { data: fetched, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useCfoRepeatEscalated();
  const rows = rowsOverride ?? fetched ?? [];

  const columns = useMemo<TableColumnDef<CFORepeatEscalatedRow>[]>(
    () => [
      {
        id: 'title',
        columnLabel: 'Issue',
        header: () => 'Issue',
        cell: ({ row }) => <span className="font-medium text-gray-900">{row.original.title}</span>,
        widthPercent: 28,
      },
      {
        id: 'department',
        columnLabel: 'Department',
        header: () => 'Department',
        cell: ({ row }) => row.original.department ?? '—',
        widthPercent: 14,
      },
      {
        id: 'period',
        columnLabel: 'Period',
        header: () => 'Period',
        cell: ({ row }) => row.original.period ?? '—',
        widthPercent: 12,
      },
      {
        id: 'status',
        columnLabel: 'Status',
        header: () => 'Status',
        cell: ({ row }) => <StatusChip value={row.original.status} />,
        widthPercent: 14,
      },
      {
        id: 'vintage_count',
        columnLabel: 'Repeat',
        header: () => 'Repeat',
        cell: ({ row }) =>
          row.original.vintage_count > 0 ? (
            <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700">
              {row.original.vintage_count}x
            </span>
          ) : (
            '—'
          ),
        widthPercent: 12,
      },
      {
        id: 'is_escalated',
        columnLabel: 'Escalated',
        header: () => 'Escalated',
        cell: ({ row }) =>
          row.original.is_escalated ? (
            <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700">
              Escalated
            </span>
          ) : (
            '—'
          ),
        widthPercent: 12,
      },
    ],
    []
  );

  return (
    <AppDataTable<CFORepeatEscalatedRow>
      data={rows}
      columns={columns}
      loading={isLoading}
      error={isError ? error?.message ?? 'Failed to load repeat/escalated issues.' : null}
      emptyMessage="No repeat or escalated issues."
      searchable
      searchPlaceholder="Search issues..."
      paginated
      defaultPageSize={10}
      headerStart={
        <div className="flex w-full justify-end">
          <RefreshMeta updatedAt={dataUpdatedAt} onRefresh={refetch} isFetching={isFetching} />
        </div>
      }
    />
  );
};

export default RepeatEscalatedTable;
